import { Howl } from 'howler';

export type SoundType =
  | 'cannon_fire'
  | 'machinegun_fire'
  | 'missile_launch'
  | 'mine_place'
  | 'explosion_small'
  | 'explosion_medium'
  | 'explosion_large'
  | 'tank_hit'
  | 'tank_destroyed'
  | 'powerup_collect'
  | 'weapon_switch'
  | 'ui_click'
  | 'engine_idle'
  | 'tank_move';

export type MusicType = 'menu' | 'battle' | 'victory' | 'gameover';

export class AudioManager {
  private sounds: Map<SoundType, Howl> = new Map();
  private music: Map<MusicType, Howl> = new Map();
  private currentMusic: Howl | null = null;
  private currentMusicType: MusicType | null = null;

  private sfxVolume: number = 0.7;
  private musicVolume: number = 0.5;
  private masterVolume: number = 1.0;

  // Web Audio API context for procedural sounds
  private audioContext: AudioContext;
  private engineOscillator: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;

  constructor() {
    // 创建 Web Audio Context
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

    // 初始化程序化音效（作为备用）
    this.initProceduralSounds();

    // 加载音效文件（如果存在）
    this.loadSounds();

    // 加载背景音乐（如果存在）
    this.loadMusic();
  }

  /**
   * 初始化程序化音效（使用 Web Audio API 生成）
   */
  private initProceduralSounds(): void {
    // 这些是备用的程序化音效，如果没有真实音频文件时使用
    console.log('🔊 Audio Manager initialized with procedural sounds');
  }

  /**
   * 加载音效文件
   */
  private loadSounds(): void {
    // 尝试加载音效文件，如果文件不存在会静默失败
    // 用户可以稍后添加音频文件到 public/sounds/ 目录

    const soundConfig: Record<SoundType, { src: string; volume?: number; rate?: number }> = {
      cannon_fire: { src: 'sounds/cannon_fire.mp3', volume: 0.6 },
      machinegun_fire: { src: 'sounds/machinegun_fire.mp3', volume: 0.4 },
      missile_launch: { src: 'sounds/missile_launch.mp3', volume: 0.5 },
      mine_place: { src: 'sounds/mine_place.mp3', volume: 0.5 },
      explosion_small: { src: 'sounds/explosion_small.mp3', volume: 0.7 },
      explosion_medium: { src: 'sounds/explosion_medium.mp3', volume: 0.8 },
      explosion_large: { src: 'sounds/explosion_large.mp3', volume: 1.0 },
      tank_hit: { src: 'sounds/tank_hit.mp3', volume: 0.6 },
      tank_destroyed: { src: 'sounds/tank_destroyed.mp3', volume: 0.8 },
      powerup_collect: { src: 'sounds/powerup_collect.mp3', volume: 0.5 },
      weapon_switch: { src: 'sounds/weapon_switch.mp3', volume: 0.3 },
      ui_click: { src: 'sounds/ui_click.mp3', volume: 0.4 },
      engine_idle: { src: 'sounds/engine_idle.mp3', volume: 0.3 },
      tank_move: { src: 'sounds/tank_move.mp3', volume: 0.4 }
    };

    Object.entries(soundConfig).forEach(([key, config]) => {
      const sound = new Howl({
        src: [config.src],
        volume: (config.volume || 0.5) * this.sfxVolume * this.masterVolume,
        rate: config.rate || 1.0,
        // 如果文件不存在，不会报错
        onloaderror: () => {
          // 静默失败，使用程序化音效
        }
      });

      this.sounds.set(key as SoundType, sound);
    });
  }

  /**
   * 加载背景音乐
   */
  private loadMusic(): void {
    const musicConfig: Record<MusicType, string> = {
      menu: 'music/menu.mp3',
      battle: 'music/battle.mp3',
      victory: 'music/victory.mp3',
      gameover: 'music/gameover.mp3'
    };

    Object.entries(musicConfig).forEach(([key, src]) => {
      const music = new Howl({
        src: [src],
        volume: this.musicVolume * this.masterVolume,
        loop: true,
        onloaderror: () => {
          // 静默失败
        }
      });

      this.music.set(key as MusicType, music);
    });
  }

  /**
   * 播放音效
   */
  playSound(type: SoundType, volumeMultiplier: number = 1.0): void {
    const sound = this.sounds.get(type);

    if (sound && sound.state() === 'loaded') {
      // 使用真实音频文件
      sound.volume(this.sfxVolume * this.masterVolume * volumeMultiplier);
      sound.play();
    } else {
      // 使用程序化音效作为备用
      this.playProceduralSound(type, volumeMultiplier);
    }
  }

  /**
   * 播放程序化生成的音效（备用方案）
   */
  private playProceduralSound(type: SoundType, volumeMultiplier: number = 1.0): void {
    const ctx = this.audioContext;
    const now = ctx.currentTime;
    const volume = this.sfxVolume * this.masterVolume * volumeMultiplier;

    switch (type) {
      case 'cannon_fire':
        this.createExplosionSound(ctx, now, volume, 80, 0.3);
        break;

      case 'machinegun_fire':
        this.createShortNoise(ctx, now, volume * 0.3, 0.05);
        break;

      case 'missile_launch':
        this.createSweepSound(ctx, now, volume, 200, 600, 0.4);
        break;

      case 'mine_place':
        this.createClickSound(ctx, now, volume, 150);
        break;

      case 'explosion_small':
        this.createExplosionSound(ctx, now, volume * 0.6, 100, 0.4);
        break;

      case 'explosion_medium':
        this.createExplosionSound(ctx, now, volume * 0.8, 70, 0.6);
        break;

      case 'explosion_large':
        this.createExplosionSound(ctx, now, volume, 50, 0.8);
        break;

      case 'tank_hit':
        this.createImpactSound(ctx, now, volume, 200);
        break;

      case 'tank_destroyed':
        this.createExplosionSound(ctx, now, volume, 60, 1.0);
        break;

      case 'powerup_collect':
        this.createPowerupSound(ctx, now, volume);
        break;

      case 'weapon_switch':
        this.createClickSound(ctx, now, volume * 0.5, 300);
        break;

      case 'ui_click':
        this.createClickSound(ctx, now, volume * 0.4, 400);
        break;

      default:
        // 默认音效
        this.createClickSound(ctx, now, volume * 0.3, 200);
    }
  }

  /**
   * 创建爆炸音效
   */
  private createExplosionSound(
    ctx: AudioContext,
    time: number,
    volume: number,
    baseFreq: number,
    duration: number
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(baseFreq, time);
    osc.frequency.exponentialRampToValueAtTime(20, time + duration);

    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, time);
    filter.frequency.exponentialRampToValueAtTime(100, time + duration);

    gain.gain.setValueAtTime(volume, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + duration);
  }

  /**
   * 创建短促噪音（机枪）
   */
  private createShortNoise(ctx: AudioContext, time: number, volume: number, duration: number): void {
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = ctx.createBufferSource();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    noise.buffer = buffer;

    filter.type = 'bandpass';
    filter.frequency.value = 1500;
    filter.Q.value = 1;

    gain.gain.setValueAtTime(volume, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    noise.start(time);
  }

  /**
   * 创建扫频音效（导弹）
   */
  private createSweepSound(
    ctx: AudioContext,
    time: number,
    volume: number,
    startFreq: number,
    endFreq: number,
    duration: number
  ): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(startFreq, time);
    osc.frequency.exponentialRampToValueAtTime(endFreq, time + duration);

    gain.gain.setValueAtTime(volume * 0.5, time);
    gain.gain.setValueAtTime(volume, time + duration * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.01, time + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + duration);
  }

  /**
   * 创建点击音效
   */
  private createClickSound(ctx: AudioContext, time: number, volume: number, freq: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(volume, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.1);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + 0.1);
  }

  /**
   * 创建撞击音效
   */
  private createImpactSound(ctx: AudioContext, time: number, volume: number, freq: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(50, time + 0.2);

    filter.type = 'lowpass';
    filter.frequency.value = 800;

    gain.gain.setValueAtTime(volume, time);
    gain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(time);
    osc.stop(time + 0.2);
  }

  /**
   * 创建道具收集音效
   */
  private createPowerupSound(ctx: AudioContext, time: number, volume: number): void {
    // 上升的音阶
    const frequencies = [262, 330, 392, 523]; // C, E, G, C
    const noteDuration = 0.08;

    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = freq;

      const startTime = time + i * noteDuration;
      gain.gain.setValueAtTime(volume * 0.3, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + noteDuration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + noteDuration);
    });
  }

  /**
   * 播放背景音乐
   */
  playMusic(type: MusicType): void {
    // 停止当前音乐
    if (this.currentMusic && this.currentMusicType !== type) {
      this.currentMusic.fade(this.currentMusic.volume(), 0, 1000);
      setTimeout(() => {
        this.currentMusic?.stop();
      }, 1000);
    }

    // 播放新音乐
    const music = this.music.get(type);
    if (music && music.state() === 'loaded') {
      this.currentMusic = music;
      this.currentMusicType = type;

      music.volume(0);
      music.play();
      music.fade(0, this.musicVolume * this.masterVolume, 1000);
    }
  }

  /**
   * 停止背景音乐
   */
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.fade(this.currentMusic.volume(), 0, 1000);
      setTimeout(() => {
        this.currentMusic?.stop();
        this.currentMusic = null;
        this.currentMusicType = null;
      }, 1000);
    }
  }

  /**
   * 暂停背景音乐
   */
  pauseMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.pause();
    }
  }

  /**
   * 恢复背景音乐
   */
  resumeMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.play();
    }
  }

  /**
   * 开始引擎声音（持续循环）
   */
  startEngineSound(): void {
    if (this.engineOscillator) return;

    const ctx = this.audioContext;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.value = 60;

    filter.type = 'lowpass';
    filter.frequency.value = 200;
    filter.Q.value = 2;

    gain.gain.value = this.sfxVolume * this.masterVolume * 0.15;

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start();

    this.engineOscillator = osc;
    this.engineGain = gain;
  }

  /**
   * 停止引擎声音
   */
  stopEngineSound(): void {
    if (this.engineOscillator) {
      this.engineOscillator.stop();
      this.engineOscillator = null;
      this.engineGain = null;
    }
  }

  /**
   * 更新引擎声音（根据速度调整音调）
   */
  updateEngineSound(speed: number): void {
    if (this.engineOscillator) {
      const baseFreq = 60;
      const maxFreq = 150;
      const targetFreq = baseFreq + (maxFreq - baseFreq) * Math.min(speed / 2, 1);

      this.engineOscillator.frequency.setValueAtTime(
        targetFreq,
        this.audioContext.currentTime
      );
    }
  }

  /**
   * 设置音效音量
   */
  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));

    // 更新所有音效的音量
    this.sounds.forEach(sound => {
      sound.volume(this.sfxVolume * this.masterVolume);
    });

    // 更新引擎声音
    if (this.engineGain) {
      this.engineGain.gain.value = this.sfxVolume * this.masterVolume * 0.15;
    }

    // 保存到 localStorage
    localStorage.setItem('sfxVolume', volume.toString());
  }

  /**
   * 设置音乐音量
   */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));

    // 更新所有音乐的音量
    this.music.forEach(music => {
      music.volume(this.musicVolume * this.masterVolume);
    });

    // 保存到 localStorage
    localStorage.setItem('musicVolume', volume.toString());
  }

  /**
   * 设置主音量
   */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));

    // 更新所有音效和音乐
    this.setSfxVolume(this.sfxVolume);
    this.setMusicVolume(this.musicVolume);

    // 保存到 localStorage
    localStorage.setItem('masterVolume', volume.toString());
  }

  /**
   * 获取音效音量
   */
  getSfxVolume(): number {
    return this.sfxVolume;
  }

  /**
   * 获取音乐音量
   */
  getMusicVolume(): number {
    return this.musicVolume;
  }

  /**
   * 获取主音量
   */
  getMasterVolume(): number {
    return this.masterVolume;
  }

  /**
   * 从 localStorage 加载音量设置
   */
  loadVolumeSettings(): void {
    const savedSfx = localStorage.getItem('sfxVolume');
    const savedMusic = localStorage.getItem('musicVolume');
    const savedMaster = localStorage.getItem('masterVolume');

    if (savedSfx !== null) {
      this.setSfxVolume(parseFloat(savedSfx));
    }

    if (savedMusic !== null) {
      this.setMusicVolume(parseFloat(savedMusic));
    }

    if (savedMaster !== null) {
      this.setMasterVolume(parseFloat(savedMaster));
    }
  }

  /**
   * 静音所有声音
   */
  muteAll(): void {
    Howler.mute(true);
    this.stopEngineSound();
  }

  /**
   * 取消静音
   */
  unmuteAll(): void {
    Howler.mute(false);
  }

  /**
   * 清理资源
   */
  dispose(): void {
    this.stopMusic();
    this.stopEngineSound();

    this.sounds.forEach(sound => sound.unload());
    this.music.forEach(music => music.unload());

    this.sounds.clear();
    this.music.clear();

    if (this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}
